/**
 * A surface that holds a record open in the app sheet.
 *
 * The surface owns the record's state, so it hands the sheet fresh content on
 * every render (`content`), or null when nothing is open. The sheet keeps its
 * width while the same surface swaps what it shows. When the sheet closes from
 * outside (its Close button, Escape), or another owner puts its own record in
 * it, `onClosed` tells the surface so it can drop the record from its state or
 * its address. Unmounting closes a sheet the surface still holds.
 */
import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { sheetStore, type SheetStore } from "./useSheet.js";

export function useOwnedSheet(
  content: ReactNode | null,
  onClosed: () => void,
  options: { wide?: boolean; store?: SheetStore } = {},
): void {
  const store = options.store ?? sheetStore;
  const owned = useRef<ReactNode | null>(null);
  const closedRef = useRef(onClosed);
  useLayoutEffect(() => {
    closedRef.current = onClosed;
  });

  useEffect(
    () =>
      store.subscribe(() => {
        const current = store.getSnapshot().content;
        if (owned.current == null || current === owned.current) return;
        // Closed, or taken by another record: either way this one is not open.
        owned.current = null;
        closedRef.current();
      }),
    [store],
  );

  const wantWide = options.wide ?? false;
  useEffect(() => {
    if (content != null) {
      const holding = owned.current != null && store.getSnapshot().content === owned.current;
      const wide = holding ? store.getSnapshot().wide : wantWide;
      owned.current = content;
      store.open(content, { wide });
      return;
    }
    if (owned.current != null) {
      const mine = store.getSnapshot().content === owned.current;
      owned.current = null;
      if (mine) store.close();
    }
  });

  useEffect(
    () => () => {
      if (owned.current != null && store.getSnapshot().content === owned.current) {
        owned.current = null;
        store.close();
      }
    },
    [store],
  );
}
